import Vue from 'vue';
import VueLS from 'vue-ls';
import VueCookies from 'vue-cookies';
import axios from 'axios';
import {
  camelCase,
  cloneDeep,
  mapKeys,
  startCase,
} from 'lodash';
import Cookies from 'js-cookie';
import { DEFAULT_PARTNER } from '@/constants/common';
import { initErrorHandler } from '@/helpers/errorHandling';
import { getPageUrlPrefix } from '@/helpers/common';
import Bugsnag from '@bugsnag/js';
import { useActions } from 'vuex-composition-helpers/dist';
import {
  CustomAxiosError,
  getFailedResponseMetadata,
  getSuccessResponseMetadata,
} from '@/helpers/bugsnag';
import ip from 'ip';

Vue.use(VueLS, { namespace: 'qz__' });
Vue.use(VueCookies);

const AUTH_TOKEN_KEY = 'quotestation_token';
const SESSION_ID_KEY = 'PHPSESSID';
const IP_ADDRESS = ip.address();

const prepareVehiclePayload = payload => {
  const newPayload = cloneDeep(payload);
  newPayload.drivers = newPayload.drivers.map((driver, index) => {
    if (index === 0) {
      driver.telephone = driver.telephone.replace('+44', '0');
      return driver;
    }
    return mapKeys(driver, (value, key) => {
      if (['claims', 'convictions'].includes(key)) return key;
      return camelCase(`additional${startCase(key)}`);
    });
  });
  return newPayload;
};

const getCredentialsKeys = (productName) => {
  const accessTokenKey = `${AUTH_TOKEN_KEY}_${productName}`;
  const phpSessIdKey = `${SESSION_ID_KEY}_${productName}`;

  return {
    accessTokenKey,
    phpSessIdKey,
  };
};

const getCredentialsFromLocalStorage = (productName) => {
  const { accessTokenKey, phpSessIdKey } = getCredentialsKeys(productName);

  return {
    accessToken: Vue.ls.get(accessTokenKey),
    phpSessId: Vue.ls.get(phpSessIdKey),
  };
};

const setCredentialsToLocalStorage = ({ productName, accessToken, phpSessId }) => {
  const { accessTokenKey, phpSessIdKey } = getCredentialsKeys(productName);

  if (accessToken) Vue.ls.set(accessTokenKey, accessToken);
  if (phpSessId) Vue.ls.set(phpSessIdKey, phpSessId);
};

const getCredentials = async (productName) => {
  const { data } = await axios.get(`http://${IP_ADDRESS}:3000/${getPageUrlPrefix(productName)}`);

  return {
    accessToken: data.accessToken,
    phpSessId: data.phpSessId,
  };
};

const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

class Api {
  constructor (productName, options) {
    this.client = options.client || axios.create({
      baseURL: options.apiBaseUrl,
      headers: defaultHeaders,
    });

    this.accessToken = options.accessToken || null;
    this.phpSessId = options.phpSessId || null;
    this.microVersion = options.microVersion;
    this.productName = productName;

    this.initResponseInterceptors();
    this.initRequestInterceptors();
  }

  initRequestInterceptors () {
    this.client.interceptors.request.use(
      config => {
        if (
          this.accessToken &&
          [
            'api/micro', // each microservice endpoint
            'api/core/quote',
            'api/lg/quote',
            'api/journey/initForm',
            'api/internalTracker/phone',
            'api/phoneValidation',
            'api/quote/confirm',
            'api/step/validate',
            'api/service/quoteUserAuth',
          ].some(url => config.url.indexOf(url) > -1)
        ) config.headers.Authorization = `Bearer ${this.accessToken}`;
        return config;
      },
      e => Promise.reject(e),
    );
  }

  initResponseInterceptors () {
    if (process.env.NODE_ENV === 'production') {
      this.client.interceptors.response.use(
        (response) => {
          Bugsnag.leaveBreadcrumb('Axios Request [success]', getSuccessResponseMetadata(response), 'request');
          return response;
        },
        async error => {
          const bugsnagMetadata = getFailedResponseMetadata(error);
          Bugsnag.leaveBreadcrumb('Axios Request [failed]', bugsnagMetadata, 'request');
          Bugsnag.notify(new CustomAxiosError(error), (event) => {
            event.addMetadata('Axios Request Error', bugsnagMetadata);
          });

          if (!error.response) {
            const { showToast } = useActions(['showToast']);

            showToast({
              type: 'error',
              text: 'Unexpected Network error',
            });

            return;
          }

          const { status, data } = error.response;

          switch (status) {
            case 440: {
              const redirectUrl = window.localStorage.getItem('initReq');

              if (redirectUrl) {
                initErrorHandler({ redirectUrl, status: status });
              } else {
                const identifyPartner = await this.identifyPartner({ product: this.productName, url: window.location.href });

                initErrorHandler({ redirectUrl: identifyPartner.data, status: status });
              }
            }
              break;
            case 409:
              initErrorHandler({ redirectUrl: data.redirectTo, status: status });
              break;
          }
          return Promise.reject(error);
        },
      );
    }
    if (process.env.NODE_ENV === 'development') {
      this.client.interceptors.response.use(
        r => r,
        error => {
          const originalRequest = error.config;
          const status = error.response.status;

          const credentials = getCredentials(this.productName);

          const isInitFromRequest = ['api/journey/initForm'].includes(originalRequest.url);

          if (status === 401 || (status === 422 && isInitFromRequest)) {
            return credentials.then(({ accessToken }) => {
              setCredentialsToLocalStorage({ productName: this.productName, accessToken });

              this.accessToken = accessToken;

              return this.client.request(originalRequest);
            });
          }

          if (status === 440) {
            return credentials.then(({ phpSessId, accessToken }) => {
              setCredentialsToLocalStorage({ productName: this.productName, accessToken, phpSessId });

              this.phpSessId = phpSessId;
              this.accessToken = accessToken;

              const data = JSON.parse(originalRequest.data);
              originalRequest.data = JSON.stringify({ ...data, phpSessId });

              return this.client.request(originalRequest);
            });
          }

          return Promise.reject(error);
        },
      );
    }
  }

  async getBusinesses (query) {
    const path = `api/micro/businesses/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      businessName: query || '???',
    });
    return data;
  }

  async getBusinessByKey (key) {
    const path = `api/micro/businesses/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      businessName: key,
      keySearch: true,
    });
    return data;
  }

  async searchPartialPostcode (query = '???') {
    const path = `api/micro/partialPostcode/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      postCode: query,
    });
    return data;
  }

  async searchFullPostcode (query = '???') {
    const path = `api/micro/postcode/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      postCode: query,
    });
    return data;
  }

  async searchBusinessTrades (
    {
      query = '???',
      microVersion = 'v2',
    },
  ) {
    const path = `api/micro/businessTrades/${microVersion}`;
    const { data } = await this.client.post(path, {
      businessTrade: query,
    });
    return data;
  }

  async searchPropertyTypes ({
    systemId,
    query = '???',
    isCommercial = null,
    microVersion = 'v2',
  }) {
    const path = `api/micro/propertyTypes/${microVersion}`;

    const { data } = await this.client.post(path, {
      propertyTypeName: query,
      isCommercial,
      systemId,
    });

    return data;
  }

  async getMotorhomeManufacturerConverter ({
    query = '???',
  }) {
    const path = `api/micro/motorhomeManufacturerConverter/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      id: query || '???',
    });
    return data;
  }

  async getHolidayHome (payload) {
    const path = `api/micro/holidayHome/${this.microVersion}`;

    const { data } = await this.client.post(
      path,
      { ...payload },
    );
    return data;
  }

  async getOccupations (query) {
    const path = `api/micro/occupations/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      occupationName: query || '???',
    });
    return data;
  }

  async getOccupationByKey (key) {
    const path = `api/micro/occupations/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      occupationName: key,
      keySearch: true,
    });
    return data;
  }

  async getTaxiAuthCodes ({ query = '???' }) {
    const path = `api/micro/taxiAuthCodes/${this.microVersion}`;
    const { data } = await this.client.post(
      path,
      { taxiAuthCode: query },
    );
    return data;
  }

  async getConvictionOffenceCodes () {
    const path = `api/micro/convictionOffenceCodes/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      systemId: 1,
    });
    return data;
  }

  async getVehicleModifications (vehicleSystemType) {
    const { data } = await this.client.post(
      `api/micro/vehicleModifications/${this.microVersion}`,
      { systemId: vehicleSystemType },
    );
    return data;
  }

  async getVehicleReg ({
    partnerName = DEFAULT_PARTNER,
    vehicleLookupPrefill = false,
    vehicleData,
    vehicleType,
    systemId,
  }) {
    const path = `api/micro/vehicleReg/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      vehicleData,
      vehicleType,
      systemId,
      partnerName,
      vehicleLookupPrefill,
    });
    return data;
  }

  async searchVehicle (vehicleSystemTypes, options) {
    const path = `api/micro/quotezoneVehicleLookup/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      systemIds: vehicleSystemTypes,
      ...options,
    });
    return data;
  }

  async getCountries ({
    query = '???',
    systemId = 39,
  }) {
    const path = `api/micro/countries/${this.microVersion}`;

    const { data } = await this.client.post(path, {
      countryName: query,
      systemId,
    });
    return data;
  }

  async getCassoaSite ({
    query = '???',
  }) {
    const path = `api/micro/cassoaSite/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      id: query || '???',
    });
    return data;
  }

  async getCaravanClub ({
    query = '???',
  }) {
    const path = `api/micro/caravanClub/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      id: query || '???',
    });
    return data;
  }

  async getTruckTypes ({
    truckCategory,
    microVersion = 'v1',
  }) {
    const path = `api/micro/truckTypes/${microVersion}`;

    const { data } = await this.client.post(path, {
      truckCategory,
    });

    return data;
  }

  async getHouseRebuildValue (fields = null) {
    const path = `api/micro/bcis/${this.microVersion}`;
    const { data } = await this.client.post(path, {
      phpSessId: this.phpSessId,
      productName: this.productName,
      fields,
    });
    return data;
  };

  async getQuote (
    productName,
    quoteData,
    productPartnerId = DEFAULT_PARTNER,
    productVersion = process.env.VUE_APP_PRODUCT_VERSION,
  ) {
    const path = 'api/core/quote';
    const { data } = await this.client.post(
      path,
      {
        productName,
        productVersion,
        productPartnerId,
        quoteData: (() => {
          switch (productName) {
            case 'car':
            case 'van':
            case 'bike':
              return prepareVehiclePayload(quoteData);
            default:
              return quoteData;
          }
        })(),
        phpSessId: this.phpSessId,
      },
    );

    return data;
  }

  async getLgQuote ({
    productName,
    quoteData,
    productPartnerId = DEFAULT_PARTNER,
    productVersion = 'v1',
  },
  ) {
    const path = 'api/lg/quote';
    const { data } = await this.client.post(
      path,
      {
        productName,
        productVersion,
        productPartnerId,
        quoteData,
        phpSessId: this.phpSessId,
      },
    );

    return data;
  }

  async processJourney ({
    productName,
    productPartnerId = DEFAULT_PARTNER,
    step,
    quoteData,
    productVersion = process.env.VUE_APP_PRODUCT_VERSION,
  }) {
    const { data } = await this.client.post(
      'api/journey/process',
      {
        productName,
        productPartnerId,
        step,
        quoteData,
        productVersion,
        phpSessId: this.phpSessId,
      },
    );
    return data;
  }

  async claimsAndConvictionValidation ({
    productName,
    productPartnerId = DEFAULT_PARTNER,
    step,
    quoteData,
    productVersion = process.env.VUE_APP_PRODUCT_VERSION,
  }) {
    const { data } = await this.client.post(
      'api/step/validate',
      {
        productName,
        productPartnerId,
        step,
        quoteData,
        productVersion,
        validation: 'step',
        phpSessId: this.phpSessId,
      },
    );

    return data;
  }

  async showJourney ({
    productName,
    productPartnerId = DEFAULT_PARTNER,
    productVersion = 'v3',
  }) {
    const { data } = await this.client.post(
      'api/journey/show',
      {
        productName,
        productPartnerId,
        productVersion,
        phpSessId: this.phpSessId,
      },
    );
    return data;
  }

  async getJourneyCookiesToSet () {
    const { data } = await this.client.post(
      'api/journey/cookiesToSet',
      { phpSessId: this.phpSessId, productName: this.productName },
    );
    return data;
  }

  async getTracking ({
    productName,
    step,
    GTMExperiments,
    productVersion = process.env.VUE_APP_PRODUCT_VERSION,
  }) {
    const { data } = await this.client.post(
      'api/journey/trackers',
      {
        productName,
        step,
        GTMExperiments,
        productVersion,
        phpSessId: this.phpSessId,
      },
    );
    return data;
  }

  async getQuoteResults ({
    sid,
    returnAll = false,
    cancelToken = null,
  }) {
    const path = `api/quote/${sid}/${this.phpSessId}`;
    const { data } = await this.client.get(
      path + `${returnAll ? '?return=all' : ''}`,
      { cancelToken },
    );
    return data;
  }

  async getQuoteResultsLg ({
    sid,
    productName,
  }) {
    const { data } = await this.client.get(`api/quote/lg/${sid}/${productName}`);

    return data;
  }

  async resubmitLgQuote ({ sid, day, time, product }) {
    const { data } = await this.client.post('api/lg/resubmit', { sid, day, time, product });
    return data;
  }

  async getBrokerSolutionResults ({
    sid,
    productType,
    partner,
  }) {
    const path = `api/quote/broker/${sid}/${productType}/${partner}`;
    const { data } = await this.client.get(path);
    return data;
  }

  async getQuoteSummary () {
    const { data } = await this.client.post(
      'api/journey/quoteSummary',
      { phpSessId: this.phpSessId, productName: this.productName },
    );
    return data;
  }

  async triggerEvent ({
    eventName,
  }) {
    const { data } = await this.client.post(
      `api/events/${eventName}`,
      { phpSessId: this.phpSessId, productName: this.productName },
    );
    return data;
  }

  async authUserQuote ({
    firstName,
    lastName,
    email,
    birthDate,
    partner,
    userKeepSignInVal,
    microVersion = 'v2',
  }) {
    const { data } = await this.client.post(
      `api/service/quoteUserAuth/${microVersion}`,
      {
        firstName,
        lastName,
        email,
        birthDate,
        partner,
        userKeepSignInVal,
        phpSessId: this.phpSessId,
      },
    );
    return data;
  }

  async userLogin ({
    email,
    password,
    passcode,
    partner,
    microVersion = 'v2',
  }) {
    const { data } = await this.client.post(
      `api/microservice/userLogin/${microVersion}`,
      {
        email,
        ...(password && { password }),
        ...(passcode && { passcode }),
        phpSessId: this.phpSessId,
        partner,
      },
    );
    return data;
  }

  async sendPasscode ({
    email,
    partner,
    microVersion = 'v1',
  }) {
    const { data } = await this.client.post(
      `api/micro/sendPasscode/${microVersion}`,
      {
        email,
        partner,
      },
    );
    return data;
  }

  async internalTracker (siteID) {
    const { data } = await this.client.post(
      `api/internalTracker/phone/${siteID}/${this.phpSessId}`,
    );
    return data;
  }

  async initForm () {
    const { data } = await this.client.post(
      'api/journey/initForm',
      { phpSessId: this.phpSessId, productName: this.productName },
    );
    return data;
  }

  async resultsPageInteraction ({
    eventKey,
    eventValue,
  }) {
    return this.client.post(
      'api/events/resultsPageInteraction',
      {
        phpSessId: this.phpSessId,
        eventKey,
        eventValue,
      },
    );
  }

  async getRelatedSystems ({
    productName,
    partner,
    currentUrl,
  }) {
    const { data } = await this.client.post(
      'api/lg/relatedSystem',
      {
        phpSessId: this.phpSessId,
        product: productName,
        partner,
        currentUrl,
      },
    );
    return data;
  }

  async getOvertureAds (productName) {
    const { data } = await this.client.post(
      'api/lg/overture',
      {
        keyword: `${productName} insurance`,
        phpSessId: this.phpSessId,
        productName: this.productName,
      },
    );
    return data;
  }

  async getLgLogos () {
    const { data } = await this.client.post(
      'api/lg/imgScroll',
      { phpSessId: this.phpSessId, productName: this.productName },
    );
    return data;
  }

  async validatePhone ({
    telephone,
    secondaryTelephone,
  }) {
    const payload = { phpSessId: this.phpSessId, productName: this.productName };
    if (telephone) payload.telephone = telephone;
    if (secondaryTelephone) payload.secondaryTelephone = secondaryTelephone;

    const { data } = await this.client.post(
      'api/phoneValidation',
      payload,
    );

    return data;
  }

  async identifyPartner ({ product, url }) {
    const { data } = await this.client.post('api/journey/identifyPartner', {
      url,
      product,
    });

    return data;
  }

  async trackCoverBoostClick (data) {
    const payload = { phpSessId: this.phpSessId, data };
    await this.client.post(
      'api/events/coverBoostClick',
      payload,
    );
  }

  async abtastyFeatureFlags (campaigns) {
    const { data } = await this.client.post('api/events/abtastyFeatureFlags', {
      phpSessId: this.phpSessId,
      campaigns,
    });

    return data;
  }

  async autoquoteConfirm (sid) {
    const { data } = await this.client.post(`api/quote/confirm/${sid}`);
    return data;
  }

  async getPetBreeds ({ petType, breedType }) {
    const path = `api/micro/petBreeds/${this.microVersion}`;

    const { data } = await this.client.post(path, {
      petType,
      breedType,
    });

    return data;
  }

  async searchTravelDestination ({ query = '???' }) {
    const path = `api/micro/destinations/${this.microVersion}`;

    const response = await this.client.post(path, {
      typeOfPolicy: 'ST',
      destinationName: query,
    });

    return response?.data || [];
  }

  async getBreakdownPrice () {
    const path = `api/micro/getBreakdownPrice/${this.microVersion}`;

    const { data } = await this.client.post(path, {
      phpSessId: this.phpSessId,
    });
    return data;
  }

  async insuranceStartDateClick (eventKey) {
    const path = 'api/events/insuranceStartDateClick';

    return await this.client.post(path, {
      phpSessId: this.phpSessId,
      eventKey,
    });
  }
}

// Create Api instance
const createApi = (productName, options) => {
  return new Api(
    productName,
    {
      apiBaseUrl: process.env.VUE_APP_QUOTESTATION_API_URL,
      microVersion: process.env.VUE_APP_MICRO_API_VERSION,
      ...options,
    });
};

export default async productName => {
  /*
    PRODUCTION
  */
  if (process.env.NODE_ENV === 'production') {
    return createApi(productName, {
      accessToken: Cookies.get(AUTH_TOKEN_KEY),
      phpSessId: Cookies.get(SESSION_ID_KEY),
    });
  }

  /*
    DEVELOPMENT
  */
  const { accessToken, phpSessId } = getCredentialsFromLocalStorage(productName);

  // Use PHPSESSID and quotestation_token from localStorage
  if (accessToken && phpSessId) {
    return createApi(productName, {
      accessToken,
      phpSessId,
    });
  }

  // Use custom scrapper if no PHPSESSID and quotestation_token in localStorage
  const credentials = await getCredentials(productName);

  // Set PHPSESSID and quotestation_token to localStorage
  setCredentialsToLocalStorage({ productName, accessToken: credentials.accessToken, phpSessId: credentials.phpSessId });

  return createApi(productName, {
    accessToken: credentials.accessToken,
    phpSessId: credentials.phpSessId,
  });
};
